import { WASHED_OUT_TRAIL_EROSION_1 } from './minigames/ErosionInvestigationConfig.js';
import { MATERIALS_WORKSHOP_SLICE } from './materials-workshop/MaterialsWorkshopConfig.js';
import { POLLINATOR_GARDEN_SLICE } from './pollinator-garden/PollinatorGardenConfig.js';

export const DISCOVERY_LAB_LAYOUT = {
  width: 1280,
  height: 720,
  compactViewportHeight: 700,
  minimumViewportWidth: 1024,
  minimumViewportHeight: 600,
} as const;

/** Home Hub still verifies every required texture before exposing this route. */
export const DISCOVERY_LAB_ROUTE_ENABLED = true;

export const DISCOVERY_LAB_DISTRICTS = {
  fieldCampus: 'field-campus',
  pollinatorGarden: 'pollinator-garden',
  earthMaterialsYard: 'landform-canyon',
  lifeCyclePond: 'river-research-station',
  plantGrowthHouse: 'greenhouse',
  habitatFieldCamp: 'habitat-preserve',
  materialsWorkshop: 'materials-workshop',
  weatherWatchStation: 'weather-observation-area',
  soundVibrationDeck: 'engineering-yard',
  discoveryObservatory: 'science-archive',
} as const;

export type DiscoveryLabDistrict =
  typeof DISCOVERY_LAB_DISTRICTS[keyof typeof DISCOVERY_LAB_DISTRICTS];

export interface DiscoveryLabDistrictDefinition {
  readonly id: DiscoveryLabDistrict;
  readonly displayName: string;
  readonly curriculumThemes: readonly string[];
  readonly openingAvailable: boolean;
}

export const DISCOVERY_LAB_DISTRICT_DEFINITIONS: readonly DiscoveryLabDistrictDefinition[] = [
  {
    id: DISCOVERY_LAB_DISTRICTS.fieldCampus,
    displayName: 'Field Campus',
    curriculumThemes: ['observation', 'evidence', 'prediction', 'effects of moving water'],
    openingAvailable: true,
  },
  {
    id: DISCOVERY_LAB_DISTRICTS.pollinatorGarden,
    displayName: 'Pollinator Garden',
    curriculumThemes: ['pollination', 'plant and animal relationships', 'evidence'],
    openingAvailable: POLLINATOR_GARDEN_SLICE.productionReady,
  },
  {
    id: DISCOVERY_LAB_DISTRICTS.earthMaterialsYard,
    displayName: 'Earth Materials Yard',
    curriculumThemes: ['soil', 'rock', 'sand', 'water observations'],
    openingAvailable: false,
  },
  {
    id: DISCOVERY_LAB_DISTRICTS.lifeCyclePond,
    displayName: 'Life Cycle Pond',
    curriculumThemes: ['plant and animal life cycles', 'sequence', 'change'],
    openingAvailable: false,
  },
  {
    id: DISCOVERY_LAB_DISTRICTS.plantGrowthHouse,
    displayName: 'Plant Growth House',
    curriculumThemes: ['light', 'water', 'growth comparison'],
    openingAvailable: false,
  },
  {
    id: DISCOVERY_LAB_DISTRICTS.habitatFieldCamp,
    displayName: 'Habitat Field Camp',
    curriculumThemes: ['needs of living things', 'habitat features'],
    openingAvailable: false,
  },
  {
    id: DISCOVERY_LAB_DISTRICTS.materialsWorkshop,
    displayName: 'Materials Workshop',
    curriculumThemes: ['observable properties of materials', 'choosing materials for a purpose'],
    openingAvailable: MATERIALS_WORKSHOP_SLICE.productionReady,
  },
  {
    id: DISCOVERY_LAB_DISTRICTS.weatherWatchStation,
    displayName: 'Weather Watch Station',
    curriculumThemes: ['clouds', 'precipitation', 'temperature patterns', 'safe observation'],
    openingAvailable: false,
  },
  {
    id: DISCOVERY_LAB_DISTRICTS.soundVibrationDeck,
    displayName: 'Sound & Vibration Deck',
    curriculumThemes: ['sound', 'vibration', 'modeled comparison'],
    openingAvailable: false,
  },
  {
    id: DISCOVERY_LAB_DISTRICTS.discoveryObservatory,
    displayName: 'Discovery Observatory',
    curriculumThemes: ['evidence', 'comparison', 'mixed mastery'],
    openingAvailable: false,
  },
] as const;

export const DISCOVERY_LAB_OPENING_SLICE = {
  id: 'washed-out-trail',
  district: DISCOVERY_LAB_DISTRICTS.fieldCampus,
  title: 'The Washed-Out Trail',
  investigation: WASHED_OUT_TRAIL_EROSION_1,
} as const;

export const DISCOVERY_LAB_POLLINATOR_SLICE = {
  ...POLLINATOR_GARDEN_SLICE,
  district: DISCOVERY_LAB_DISTRICTS.pollinatorGarden,
} as const;

/** Production-ready third investigation unlocked by the restored Pollinator Garden. */
export const DISCOVERY_LAB_MATERIALS_SLICE = {
  ...MATERIALS_WORKSHOP_SLICE,
  district: DISCOVERY_LAB_DISTRICTS.materialsWorkshop,
} as const;
